<template>
  <section class="invitation-admin-card" aria-labelledby="invitation-admin-title">
    <header class="invitation-admin-header">
      <div class="invitation-admin-mark" aria-hidden="true">
        <i class="material-icons">mail</i>
      </div>
      <div class="invitation-admin-heading">
        <p class="invitation-admin-eyebrow">VOLUNTEER INVITATIONS</p>
        <h2 id="invitation-admin-title">{{ scope === 'all' ? 'Invitation overview' : 'Invitation activity' }}</h2>
        <p>{{ scope === 'all' ? 'See who is inviting, who has joined, and where invitations are making a difference.' : 'A location-level view of invitations and the people who have joined.' }}</p>
      </div>
      <div class="invitation-admin-actions">
        <span v-if="enabled !== null" class="invitation-enabled" :class="enabled ? 'is-enabled' : 'is-disabled'">
          <span aria-hidden="true"></span>{{ enabled ? 'Invitations on' : 'Invitations off' }}
        </span>
        <button class="invitation-refresh" type="button" aria-label="Refresh invitation activity" title="Refresh"
          :disabled="loading" @click="load(1)">
          <i class="material-icons" :class="{ 'is-spinning': loading }">refresh</i>
        </button>
      </div>
    </header>

    <form v-if="scope === 'all'" class="invitation-filters" @submit.prevent="load(1)">
      <label>
        <span>Organization</span>
        <select v-model="filters.organizationId" @change="onOrganizationChange">
          <option value="">All organizations</option>
          <option v-for="organization in organizations" :key="organization.id" :value="String(organization.id)">
            {{ organization.name }}
          </option>
        </select>
      </label>
      <label>
        <span>Location</span>
        <select v-model="filters.locationId" @change="load(1)">
          <option value="">All locations</option>
          <option v-for="location in locations" :key="location.id" :value="String(location.id)">
            {{ location.name }}<template v-if="!filters.organizationId && location.organization_name"> · {{ location.organization_name }}</template>
          </option>
        </select>
      </label>
      <label class="invitation-search">
        <span>Find an invitation</span>
        <div>
          <input v-model.trim="filters.search" type="search" maxlength="100" placeholder="Name, email, inviter…" />
          <button type="submit" aria-label="Search invitations" title="Search">
            <i class="material-icons">search</i>
          </button>
        </div>
      </label>
    </form>

    <p v-if="error" class="invitation-admin-error" role="alert">{{ error }}</p>

    <div class="invitation-metrics" aria-label="Invitation statistics">
      <div class="invitation-metric">
        <span class="invitation-metric-icon sent"><i class="material-icons" aria-hidden="true">outgoing_mail</i></span>
        <div><strong>{{ summary.sent }}</strong><span>Sent</span></div>
      </div>
      <div class="invitation-metric">
        <span class="invitation-metric-icon joined"><i class="material-icons" aria-hidden="true">person_add_alt</i></span>
        <div><strong>{{ summary.signed_up }}</strong><span>Invited sign-ups</span></div>
      </div>
      <div class="invitation-metric">
        <span class="invitation-metric-icon pending"><i class="material-icons" aria-hidden="true">hourglass_top</i></span>
        <div><strong>{{ summary.pending }}</strong><span>In progress</span></div>
      </div>
      <div class="invitation-metric">
        <span class="invitation-metric-icon failed"><i class="material-icons" aria-hidden="true">mark_email_unread</i></span>
        <div><strong>{{ summary.failed }}</strong><span>Not delivered</span></div>
      </div>
      <div class="invitation-conversion">
        <strong>{{ summary.conversion_rate }}%</strong>
        <span>of sent invitations led to sign-up</span>
      </div>
    </div>

    <p v-if="enabled !== null" class="invitation-setting-note">
      Manage availability in <strong>Location Settings</strong> by changing “Allow volunteers to invite others to serve.”
    </p>

    <div v-if="loading" class="invitation-table-state" role="status">
      <span class="spinner-border spinner-border-sm text-success" aria-hidden="true"></span>
      Loading invitation activity…
    </div>
    <div v-else-if="invitations.length === 0" class="invitation-table-state invitation-empty">
      <i class="material-icons" aria-hidden="true">mark_email_read</i>
      <div><strong>No invitations found</strong><span>Try another filter, or check back after the next invitation is sent.</span></div>
    </div>
    <div v-else class="table-responsive invitation-table-wrap">
      <table class="invitation-table">
        <thead>
          <tr>
            <th scope="col">Invited person</th>
            <th scope="col">Invited by</th>
            <th v-if="scope === 'all'" scope="col">Organization / location</th>
            <th scope="col">Suggested roles</th>
            <th scope="col">Sent</th>
            <th scope="col">Outcome</th>
            <th scope="col"><span class="visually-hidden">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invitation in invitations" :key="invitation.id">
            <td>
              <strong class="invitation-person-name">{{ fullName(invitation) }}</strong>
              <span class="invitation-secondary">{{ invitation.email }}</span>
            </td>
            <td>{{ invitation.inviter_name || '—' }}</td>
            <td v-if="scope === 'all'">
              <strong class="invitation-place-name">{{ invitation.organization_name || 'Organization' }}</strong>
              <span class="invitation-secondary">{{ invitation.location_name }}</span>
            </td>
            <td>
              <span v-if="invitation.skills.length" class="invitation-skills" :title="invitation.skills.join(', ')">
                {{ invitation.skills.join(', ') }}
              </span>
              <span v-else class="invitation-secondary">General invitation</span>
            </td>
            <td>{{ formatDate(invitation.sent_at || invitation.created_at) }}</td>
            <td>
              <span class="invitation-outcome" :class="`outcome-${outcomeClass(invitation)}`">
                {{ outcomeLabel(invitation) }}
              </span>
              <span v-if="invitation.registered_at && invitation.status !== 'cancelled'" class="invitation-secondary invitation-outcome-date">
                Joined {{ formatDate(invitation.registered_at) }}
              </span>
            </td>
            <td>
              <button v-if="canCancel(invitation)" type="button" class="invitation-cancel" :disabled="cancellingId === invitation.id"
                :aria-label="`Cancel the invitation for ${fullName(invitation)}`" @click="cancelInvitation(invitation)">
                {{ cancellingId === invitation.id ? 'Cancelling…' : 'Cancel' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <footer v-if="lastPage > 1" class="invitation-pagination">
      <span>{{ totalInvitations }} invitations · Page {{ page }} of {{ lastPage }}</span>
      <div>
        <button type="button" :disabled="page <= 1 || loading" @click="load(page - 1)">Previous</button>
        <button type="button" :disabled="page >= lastPage || loading" @click="load(page + 1)">Next</button>
      </div>
    </footer>
  </section>
</template>

<script>
import api from '@/config/api';

export default {
  name: 'VolunteerInvitationAdminCard',
  props: {
    scope: { type: String, default: 'location', validator: value => ['location', 'all'].includes(value) },
    locationId: { type: [String, Number], default: null },
  },
  data() {
    return {
      enabled: null,
      organizations: [],
      locations: [],
      invitations: [],
      loading: false,
      error: '',
      page: 1,
      lastPage: 1,
      totalInvitations: 0,
      cancellingId: null,
      requestId: 0,
      filters: { organizationId: '', locationId: '', search: '' },
      summary: { total: 0, sent: 0, signed_up: 0, pending: 0, failed: 0, conversion_rate: 0 },
    };
  },
  watch: {
    locationId: {
      immediate: true,
      handler() {
        if (this.scope === 'location' && this.locationId) this.load(1);
      },
    },
  },
  mounted() {
    if (this.scope === 'all') this.load(1);
  },
  methods: {
    async load(page) {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = '';
      const params = { page, per_page: 15 };
      if (this.scope === 'location') {
        params.location_id = this.locationId;
      } else {
        if (this.filters.organizationId) params.organization_id = this.filters.organizationId;
        if (this.filters.locationId) params.location_id = this.filters.locationId;
        if (this.filters.search) params.search = this.filters.search;
      }

      try {
        const response = await api.get('/volunteer-invitations/admin', { params });
        if (requestId !== this.requestId) return;
        const data = response.data?.data || {};
        this.enabled = data.enabled === null || data.enabled === undefined ? null : data.enabled === true;
        this.organizations = data.organizations || [];
        this.locations = data.locations || [];
        this.invitations = data.invitations || [];
        this.summary = { ...this.summary, ...(data.summary || {}) };
        this.page = data.pagination?.current_page || 1;
        this.lastPage = data.pagination?.last_page || 1;
        this.totalInvitations = data.pagination?.total || 0;
      } catch (error) {
        if (requestId === this.requestId) {
          this.invitations = [];
          this.error = error.response?.data?.message || 'Invitation activity could not be loaded.';
        }
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    onOrganizationChange() {
      this.filters.locationId = '';
      this.load(1);
    },
    fullName(invitation) {
      return `${invitation.firstname || ''} ${invitation.lastname || ''}`.trim() || 'Unnamed invitee';
    },
    outcomeClass(invitation) {
      if (invitation.status === 'cancelled') return 'cancelled';
      if (invitation.signed_up) return 'joined';
      if (invitation.status === 'failed') return 'failed';
      if (invitation.status === 'pending') return 'pending';
      return 'sent';
    },
    outcomeLabel(invitation) {
      if (invitation.status === 'cancelled') return 'Cancelled';
      if (invitation.signed_up) return invitation.status === 'accepted' ? 'Accepted' : 'Signed up';
      return ({ sent: 'Sent', pending: 'In progress', failed: 'Not delivered' })[invitation.status] || invitation.status;
    },
    canCancel(invitation) {
      return ['pending', 'sent', 'registered', 'failed'].includes(invitation.status);
    },
    async cancelInvitation(invitation) {
      if (this.cancellingId !== null) return;
      if (!window.confirm(`Cancel the invitation for ${this.fullName(invitation)}? The link in their email will stop working.`)) return;
      this.cancellingId = invitation.id;
      try {
        await api.delete(`/volunteer-invitations/${invitation.id}`);
        await this.load(this.page);
      } catch (error) {
        const message = error.response?.data?.message || 'The invitation could not be cancelled.';
        if (error.response?.status === 409) {
          await this.load(this.page);
        }
        this.error = message;
      } finally {
        this.cancellingId = null;
      }
    },
    formatDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? '—'
        : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
    },
  },
};
</script>

<style scoped>
.invitation-admin-card { overflow: hidden; border: 1px solid #dce7df; border-radius: 8px; background: #fff; color: #293a30; box-shadow: 0 5px 18px rgba(28, 66, 43, .06); }
.invitation-admin-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px; border-bottom: 1px solid #e8eee9; background: linear-gradient(110deg, #f0f7f1, #fff 72%); }
.invitation-admin-mark { display: grid; width: 42px; height: 42px; flex: 0 0 42px; place-items: center; border-radius: 10px; background: #216b4b; color: #fff; }
.invitation-admin-mark .material-icons { font-size: 21px; }
.invitation-admin-heading { min-width: 0; }
.invitation-admin-eyebrow { margin: 0 0 2px; color: #688672; font-size: 10px; font-weight: 800; letter-spacing: .08em; }
.invitation-admin-heading h2 { margin: 0; color: #294b36; font-size: 17px; font-weight: 750; }
.invitation-admin-heading > p:last-child { margin: 3px 0 0; color: #77847a; font-size: 12px; }
.invitation-admin-actions { display: flex; align-items: center; gap: 10px; margin-left: auto; }
.invitation-enabled { display: inline-flex; align-items: center; gap: 6px; padding: 5px 9px; border-radius: 999px; font-size: 11px; font-weight: 700; white-space: nowrap; }
.invitation-enabled > span { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.is-enabled { background: #e5f3e9; color: #287347; }
.is-disabled { background: #f2eeea; color: #74695e; }
.invitation-refresh { display: grid; width: 32px; height: 32px; place-items: center; border: 1px solid #d9e4db; border-radius: 6px; background: #fff; color: #4c7258; }
.invitation-refresh:disabled { opacity: .55; }
.invitation-refresh .material-icons { font-size: 18px; }
.is-spinning { animation: invitation-spin .8s linear infinite; }
@keyframes invitation-spin { to { transform: rotate(360deg); } }
.invitation-filters { display: grid; grid-template-columns: minmax(180px, 1fr) minmax(180px, 1fr) minmax(220px, 1.3fr); gap: 12px; padding: 14px 20px; border-bottom: 1px solid #edf1ed; }
.invitation-filters label { display: block; min-width: 0; margin: 0; }
.invitation-filters label > span { display: block; margin-bottom: 4px; color: #66776b; font-size: 10px; font-weight: 700; }
.invitation-filters select,.invitation-search input { width: 100%; min-height: 36px; padding: 6px 9px; border: 1px solid #dce5de; border-radius: 5px; background: #fff; color: #34473a; font-size: 12px; }
.invitation-search > div { display: flex; }
.invitation-search input { border-radius: 5px 0 0 5px; }
.invitation-search button { display: grid; width: 38px; flex: 0 0 38px; place-items: center; border: 1px solid #216b4b; border-radius: 0 5px 5px 0; background: #216b4b; color: #fff; }
.invitation-search button .material-icons { font-size: 18px; }
.invitation-metrics { display: grid; grid-template-columns: repeat(4, minmax(120px, 1fr)) minmax(140px, 1.2fr); align-items: center; gap: 10px; padding: 15px 20px; border-bottom: 1px solid #edf1ed; }
.invitation-metric { display: flex; min-width: 0; align-items: center; gap: 9px; }
.invitation-metric-icon { display: grid; width: 32px; height: 32px; flex: 0 0 32px; place-items: center; border-radius: 8px; background: #eef4ef; color: #52745a; }
.invitation-metric-icon .material-icons { font-size: 17px; }
.invitation-metric-icon.joined { background: #e7f4ea; color: #287347; }
.invitation-metric-icon.pending { background: #f5f1e5; color: #8a7438; }
.invitation-metric-icon.failed { background: #f8ecea; color: #a45043; }
.invitation-metric > div { display: flex; min-width: 0; flex-direction: column; }
.invitation-metric strong { color: #34483a; font-size: 17px; line-height: 1.1; }
.invitation-metric > div > span { color: #78857b; font-size: 10px; white-space: nowrap; }
.invitation-conversion { padding-left: 14px; border-left: 1px solid #e7ece8; }
.invitation-conversion strong { display: block; color: #216b4b; font-size: 18px; line-height: 1.1; }
.invitation-conversion span { color: #78857b; font-size: 10px; }
.invitation-setting-note { margin: 0; padding: 10px 20px; border-bottom: 1px solid #edf1ed; color: #7b887e; font-size: 11px; }
.invitation-setting-note strong { color: #5f7364; font-weight: 700; }
.invitation-admin-error { margin: 14px 20px 0; padding: 9px 11px; border-radius: 5px; background: #fff0ed; color: #963f33; font-size: 12px; }
.invitation-table-wrap { padding: 0 20px; }
.invitation-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.invitation-table th { padding: 10px 9px; border-bottom: 1px solid #e8eee9; color: #718075; font-size: 10px; font-weight: 800; text-align: left; text-transform: uppercase; white-space: nowrap; }
.invitation-table td { max-width: 240px; padding: 11px 9px; border-bottom: 1px solid #eef2ee; color: #46594b; vertical-align: middle; }
.invitation-table tbody tr:last-child td { border-bottom: 0; }
.invitation-person-name,.invitation-place-name { display: block; overflow: hidden; color: #34483a; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.invitation-secondary { display: block; overflow: hidden; margin-top: 2px; color: #859087; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.invitation-skills { display: -webkit-box; overflow: hidden; color: #627267; font-size: 11px; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.invitation-outcome { display: inline-block; padding: 4px 7px; border-radius: 999px; background: #edf2ee; color: #5a705e; font-size: 10px; font-weight: 750; white-space: nowrap; }
.outcome-joined { background: #e5f4e9; color: #287347; }
.outcome-pending { background: #f6f1e3; color: #816d34; }
.outcome-failed { background: #faeae8; color: #a34b40; }
.outcome-cancelled { background: #eceeed; color: #6b7570; }
.invitation-cancel { padding: 3px 9px; border: 1px solid #e3b8b3; border-radius: 6px; background: #fff; color: #a34b40; font-size: 11px; font-weight: 700; cursor: pointer; }
.invitation-cancel:hover:not(:disabled) { background: #faeae8; }
.invitation-cancel:disabled { opacity: .6; cursor: default; }
.invitation-outcome-date { max-width: 150px; }
.invitation-table-state { display: flex; min-height: 76px; align-items: center; justify-content: center; gap: 8px; color: #7b887e; font-size: 12px; }
.invitation-empty { justify-content: flex-start; padding: 14px 20px; }
.invitation-empty > .material-icons { color: #72917a; font-size: 24px; }
.invitation-empty strong,.invitation-empty span { display: block; }
.invitation-empty strong { color: #53685a; font-size: 12px; }
.invitation-empty span { margin-top: 2px; color: #879188; font-size: 11px; }
.invitation-pagination { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 20px; border-top: 1px solid #edf1ed; color: #7b887e; font-size: 11px; }
.invitation-pagination > div { display: flex; gap: 6px; }
.invitation-pagination button { padding: 5px 8px; border: 1px solid #dce5de; border-radius: 4px; background: #fff; color: #476b53; font-size: 11px; font-weight: 700; }
.invitation-pagination button:disabled { color: #a9b1aa; }
@media (max-width: 900px) { .invitation-metrics { grid-template-columns: repeat(2, minmax(130px, 1fr)); } .invitation-conversion { padding-left: 0; border-left: 0; } }
@media (max-width: 640px) { .invitation-admin-header { align-items: flex-start; padding: 14px; } .invitation-admin-mark { width: 36px; height: 36px; flex-basis: 36px; } .invitation-admin-heading h2 { font-size: 15px; } .invitation-admin-heading > p:last-child { max-width: 240px; font-size: 11px; } .invitation-admin-actions { flex-direction: column-reverse; gap: 5px; } .invitation-enabled { padding: 4px 6px; font-size: 9px; } .invitation-filters { grid-template-columns: 1fr; padding: 12px 14px; } .invitation-metrics { gap: 12px 8px; padding: 12px 14px; } .invitation-metric { gap: 6px; } .invitation-metric-icon { width: 28px; height: 28px; flex-basis: 28px; } .invitation-metric strong { font-size: 15px; } .invitation-metric > div > span { font-size: 9px; } .invitation-table-wrap { padding: 0 10px; } .invitation-table th,.invitation-table td { padding-right: 6px; padding-left: 6px; } .invitation-pagination { padding: 10px 14px; } }
@media (prefers-reduced-motion: reduce) { .is-spinning { animation: none; } }
</style>